import { CSSProperties, FC } from 'react';
import { IconType } from 'react-icons';
import {
    TbBarrierBlock,
    TbCircle,
    TbCircleCheckFilled,
    TbCircleDot,
    TbCircleXFilled,
    TbEyeCheck,
    TbInbox,
    TbProgress,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { TaskStatus, TaskStatusCategory, TaskStatusSystemKey } from '@/types';

type TaskStatusIconStatus = Pick<TaskStatus, 'category' | 'color' | 'systemKey'>;

interface TaskStatusIconProps {
    size?: number | string;
    status?: TaskStatusIconStatus | null;
    style?: CSSProperties;
}

const statusIcons: Record<TaskStatusSystemKey, IconType> = {
    [TaskStatusSystemKey.BACKLOG]: TbInbox,
    [TaskStatusSystemKey.TODO]: TbCircle,
    [TaskStatusSystemKey.IN_PROGRESS]: TbProgress,
    [TaskStatusSystemKey.IN_REVIEW]: TbEyeCheck,
    [TaskStatusSystemKey.BLOCKED]: TbBarrierBlock,
    [TaskStatusSystemKey.COMPLETED]: TbCircleCheckFilled,
    [TaskStatusSystemKey.CANCELED]: TbCircleXFilled,
};

const categoryIcons: Record<TaskStatusCategory, IconType> = {
    [TaskStatusCategory.ACTIVE]: TbCircleDot,
    [TaskStatusCategory.COMPLETED]: TbCircleCheckFilled,
    [TaskStatusCategory.CANCELED]: TbCircleXFilled,
};

const TaskStatusIcon: FC<TaskStatusIconProps> = ({ size = '0.75rem', status, style }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const color = status?.color ?? token.colorTextTertiary;
    const Icon =
        (status?.systemKey && statusIcons[status.systemKey]) ||
        (status?.category && categoryIcons[status.category]) ||
        TbCircleDot;

    return (
        <span
            aria-hidden
            style={{
                alignItems: 'center',
                background: `color-mix(in srgb, ${color} 14%, transparent)`,
                border: `1px solid color-mix(in srgb, ${color} 38%, transparent)`,
                borderRadius: token.borderRadiusSM,
                color,
                display: 'inline-flex',
                flex: '0 0 auto',
                height: '0.875rem',
                justifyContent: 'center',
                width: '0.875rem',
                ...style,
            }}
        >
            <Icon size={size} />
        </span>
    );
};

export default TaskStatusIcon;
